import {
    APP_ID,
    DEFAULT_CURRENCY_CODE,
    importProvidersFrom,
    isDevMode,
    LOCALE_ID,
    inject,
    provideAppInitializer,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
    BrowserModule,
    provideClientHydration,
    withNoHttpTransferCache,
} from '@angular/platform-browser';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { SharedModule } from 'shared/shared.module';
import { VirsekerSharedModule } from 'shared/virseker-shared.module';
import { BaseModule } from 'templates/base.module';
import { Subject } from 'rxjs';
import { CoreModule } from 'templates/core/core.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AppUpdateService } from 'sharedServices/app-update.service';
import { en_GB, NZ_DATE_LOCALE, NZ_I18N } from 'ng-zorro-antd/i18n';
import { LanguageLocales } from 'sharedEnums/language-locales';
import { provideAnimations } from '@angular/platform-browser/animations';
import { af } from 'date-fns/locale';
import {
    DropOffService_Token,
    LeadNumberProvider_Token,
    NavigationService_Token,
    QuoteControllerService_Token,
} from 'shared/base/generics/injection-tokens/dependency-tokens';
import { QuoteControllerService } from 'services/quote-controller.service';
import { DropOffService } from 'services/dropoff.service';
import { NavigationService } from 'services/navigation/navigation.service';
import { DynamicComponentService } from 'sharedGlobals/dynamic-component/dynamic-component.service';
import { ScriptInjectionService } from 'sharedGlobals/gtm-data-layer/script-injection.service';
import { QuoteLeadNumberProviderService } from 'services/quote-lead-number-provider.service';
import { environment } from '../environments/environment';
import { VirsekerAppInitService } from './virseker-app-init.service';
import { VirsekerRoutingModule } from './virseker-routing.module';

const appInitializerFn = (appInitService$: VirsekerAppInitService) => {
    inject(AppUpdateService);
    inject(DynamicComponentService);
    inject(ScriptInjectionService);
    return async () => {
        return await appInitService$.appInit();
    };
};

export const appConfig = {
    providers: [
        importProvidersFrom(
            CommonModule,
            BrowserModule,
            StoreDevtoolsModule.instrument({
                maxAge: 125,
                logOnly: environment.production,
                connectInZone: true,
            }),
            SharedModule,
            VirsekerSharedModule,
            // BaseModule does App config and imports and setups the store. This is for stuff only needed on the root app level
            BaseModule.forRoot({
                environment,
                errorModal: {
                    create: () => {},
                    instance: new Subject<any>(),
                    isOpen: false,
                    title: 'Hier is fout',
                },
                gtmId: environment.gtmId,
                fbId: environment.fbIds,
                urlHeaderWhiteList: [environment.apiUrl],
            }),
            VirsekerRoutingModule,
            // NB! Core module imports modules that also need to be imported into Lazy Modules
            CoreModule,
            FormsModule,
            ReactiveFormsModule,
            ServiceWorkerModule.register('ngsw-worker.js', {
                enabled: !isDevMode(),
                registrationStrategy: 'registerImmediately',
            }),
            NgOptimizedImage,
        ),
        provideClientHydration(withNoHttpTransferCache()),
        { provide: APP_ID, useValue: 'virseker-confusion-app' },
        VirsekerAppInitService,
        provideAppInitializer(() => {
            const initializerFn = appInitializerFn(inject(VirsekerAppInitService));
            return initializerFn();
        }),
        NzModalService,
        AppUpdateService,
        { provide: QuoteControllerService_Token, useClass: QuoteControllerService },
        { provide: DropOffService_Token, useClass: DropOffService },
        { provide: NavigationService_Token, useClass: NavigationService },
        {
            provide: LeadNumberProvider_Token,
            useClass: QuoteLeadNumberProviderService,
        },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'ZAR' },
        { provide: LOCALE_ID, useValue: 'en-ZA' },
        { provide: NZ_I18N, useValue: en_GB },
        { provide: NZ_DATE_LOCALE, useValue: af },
        {
            provide: 'containerConfig',
            useValue: {
                maxWidth: 'max-w-screen-xs',
            },
        },
        {
            provide: 'baseOptionConfig',
            useValue: {
                defaultClasses:
                    'flex flex-col justify-center cursor-pointer option-container font-bold',
                bgClass: 'bg-virseker-grey-15 option-container',
                bgClassSelected: 'selected bg-virseker-blue option-container text-virseker-white',
                bgHover: 'hover:bg-sub-2',
                paddingClass: 'px-4 py-3 sm:py-4',
                mainTextClass: 'leading-4 md:leading-5',
            },
        },
        {
            provide: 'datepickerConfig',
            useValue: {
                language: LanguageLocales.AF,
                dayText: 'Dag',
                monthText: 'Maand',
                yearText: 'Jaar',
            },
        },
        provideAnimations(),
    ],
};
